import LazyLoad from 'react-lazy-load';
import React from 'react';
import TextArea from '../../../common/TextArea/TextArea';
import { Box, Divider, Grid, List, ListItem, MenuItem, TextField, Typography } from '@mui/material';
import { PossibleQuestion } from '../../../interfaces/surveyScreen';
import { useTranslation } from 'react-i18next';
import { ViewAllStyles } from '../../../styles/ViewAllScreenStyles';

interface AdditionalQuestionsSectionProps {
  additionalQuestions: Array<PossibleQuestion>;
  isValidating: boolean;
  IRSecLength: number;
  backButtonRef: React.RefObject<HTMLButtonElement>;
  nextButtonRef: React.RefObject<HTMLButtonElement>;
  updateResponse: (data: string, qseq?: number | string) => Promise<string>;
  onInputChange: (value: string, id?: string) => void;
}

const AdditionalQuestionsSection = ({
  additionalQuestions,
  IRSecLength,
  isValidating,
  updateResponse,
  onInputChange,
  nextButtonRef,
  backButtonRef,
}: AdditionalQuestionsSectionProps): React.JSX.Element => {
  const { classes } = ViewAllStyles();
  const { t } = useTranslation();

  const inputChangeHandler =
    (qseq: number | string) =>
    (event: React.ChangeEvent<{ value: string }>): void => {
      updateResponse(event.target.value.toString(), qseq);
    };

  // eslint-disable-next-line react/display-name
  const MCComponent = React.memo(
    ({ ques: { selectedResponse, options }, id }: { ques: PossibleQuestion; id: number }) => {
      return (
        <Grid item lg={7} sm={12} className={classes.width100}>
          <TextField
            id="MCQQuestion"
            select={true}
            value={selectedResponse || '-1'}
            disabled={false}
            fullWidth
            error={isValidating && !selectedResponse}
            size="medium"
            onChange={inputChangeHandler(IRSecLength + id + 1)}
            variant="outlined"
            InputProps={{
              className: classes.inputBG,
              id: 'mcqQuestionInput',
            }}>
            <MenuItem value="-1">{t('pleaseSelect')}</MenuItem>
            {options &&
              options.map((lbl) => (
                <MenuItem key={lbl.sequence} value={lbl.sequence}>
                  {lbl.text}
                </MenuItem>
              ))}
          </TextField>
          {isValidating && !selectedResponse && (
            <Box className={classes.errorText} pt={2}>
              <Typography>{t('enterResponse')}</Typography>
            </Box>
          )}
        </Grid>
      );
    },
  );

  return (
    <List>
      {additionalQuestions &&
        additionalQuestions.map((ques: PossibleQuestion, id: number) => {
          return (
            <LazyLoad key={`${ques.itemID || ques.itemId}_${ques.scaleId}`}>
              <ListItem key={id}>
                <Box className={classes.listItem}>
                  <Divider aria-hidden="true" className={classes.divider} />
                  <Box className={`${classes.questionBlock}`}>
                    <Grid container>
                      <Grid item lg={ques.type === 'MC' ? 5 : 12} sm={12}>
                        <div className={classes.flexgrow1}>
                          <Typography className={classes.font1rem}>{ques.questionText}</Typography>
                        </div>
                      </Grid>

                      {ques.type === 'MC' && <MCComponent ques={ques} id={id} />}
                      {ques.type === 'OQ' && (
                        <Grid item lg={12} sm={12} className={classes.width100} data-tempid={IRSecLength + id + 1}>
                          <TextArea
                            cls={classes.commentBox}
                            value={ques.selectedResponse || ''}
                            charLimit={3000}
                            onValueChange={onInputChange}
                            nextButtonRef={nextButtonRef}
                            backButtonRef={backButtonRef}
                            itemId={ques.itemID || ques.itemId}
                            questionSequence={IRSecLength + id + 1}
                            showError={
                              !!(isValidating && !ques.selectedResponse && ques.questMust && ques.questMust < 0)
                            }
                          />
                          {ques.questMust && ques.questMust < 0 && isValidating && !ques.selectedResponse ? (
                            <Box className={classes.errorText}>
                              <Typography>{t('enterResponse')}</Typography>
                            </Box>
                          ) : null}
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                  <Divider
                    aria-hidden="true"
                    className={`${id === additionalQuestions.length - 1 ? classes.finalDivider : classes.displayNone} ${
                      classes.divider
                    }`}
                  />
                </Box>
              </ListItem>
            </LazyLoad>
          );
        })}
    </List>
  );
};

export default AdditionalQuestionsSection;
